import React from 'react';
import { estadoEmision, tipoOperacionArt, subOperacionArt } from '../../../data/listasDesplegables';

/**
 * SectionDetalleArt - Componente modular para los datos principales de emisión de ART.
 * Incluye comprobación asíncrona en segundo plano y cartel de aviso por duplicidad de N° de Contrato.
 */
const SectionDetalleArt = ({ state, actions }) => {
    const { 
        formData, 
        isValidatingContrato, 
        contratoDuplicado 
    } = state;

    const { 
        handleChange, 
        validarContratoDuplicado 
    } = actions;

    return (
        <fieldset className="border border-slate-200 rounded-xl p-6 bg-white shadow-sm text-left animate-fade-in transition-all duration-300">
            <legend className="px-3 text-base font-bold text-slate-700 tracking-tight">Detalles de la Emisión</legend>
            
            {/* FILA 1: CARGA, OPERACIÓN, SUB OPERACIÓN E INICIO */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 pt-2">
                
                {/* FECHA DE CARGA */}
                <div>
                    <label className="label-form text-slate-400 font-bold mb-1 block">Fecha de Carga</label>
                    <input 
                        type="date" 
                        name="fechaCarga" 
                        value={formData.fechaCarga || ''} 
                        onChange={handleChange} 
                        className="input-round bg-slate-50/70 border-slate-200 text-slate-400 cursor-not-allowed w-full rounded-xl px-4 py-2.5 h-[46px] outline-none border transition-all" 
                        readOnly 
                    />
                </div>

                {/* TIPO DE OPERACIÓN */}
                <div>
                    <label className="label-form text-slate-600 font-bold mb-1 block">Tipo Operación *</label>
                    <select 
                        name="tipoOperacion" 
                        value={formData.tipoOperacion || ''} 
                        onChange={handleChange} 
                        className="input-round border-slate-200 font-bold w-full rounded-xl px-4 py-2.5 h-[46px] outline-none border transition-all focus:border-indigo-500 focus:ring-1 focus:ring-indigo-100" 
                        required
                    >
                        <option value="">Seleccione...</option>
                        {tipoOperacionArt.map(m => (
                            <option key={m} value={m}>{m}</option>
                        ))}
                    </select>
                </div>

                {/* SUB OPERACIÓN */}
                <div>
                    <label className="label-form text-slate-600 font-bold mb-1 block">Sub Operación *</label>
                    <select 
                        name="subOperacion" 
                        value={formData.subOperacion || ''} 
                        onChange={handleChange} 
                        className="input-round border-slate-200 font-bold w-full rounded-xl px-4 py-2.5 h-[46px] outline-none border transition-all focus:border-indigo-500 focus:ring-1 focus:ring-indigo-100" 
                        required
                    >
                        <option value="">Seleccione...</option>
                        {subOperacionArt.map(sm => (
                            <option key={sm} value={sm}>{sm}</option>
                        ))}
                    </select>
                </div>

                {/* INICIO VIGENCIA */}
                <div>
                    <label className="label-form text-indigo-700 font-bold text-[10px] uppercase tracking-wider mb-1 block">Inicio Vigencia *</label>
                    <input 
                        name="inicioVigencia" 
                        type="date" 
                        value={formData.inicioVigencia || ''} 
                        onChange={handleChange} 
                        className="input-round border-indigo-200 font-bold text-indigo-700 bg-indigo-50/10 focus:bg-white w-full rounded-xl px-4 py-2.5 h-[46px] outline-none border transition-all focus:border-indigo-500 focus:ring-1 focus:ring-indigo-100" 
                        required 
                    />
                </div>
            </div>

            {/* FILA 2: FIN, ESTADO Y N° CONTRATO */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-6 items-start">
                
                {/* FIN VIGENCIA */}
                <div>
                    <label className="label-form text-indigo-700 font-bold text-[10px] uppercase tracking-wider mb-1 block">Fin Vigencia *</label>
                    <input 
                        name="finVigencia" 
                        type="date" 
                        value={formData.finVigencia || ''} 
                        onChange={handleChange} 
                        className="input-round border-indigo-200 font-bold text-indigo-700 bg-indigo-50/10 focus:bg-white w-full rounded-xl px-4 py-2.5 h-[46px] outline-none border transition-all focus:border-indigo-500 focus:ring-1 focus:ring-indigo-100" 
                        required 
                    />
                </div>

                {/* ESTADO DE PÓLIZA */}
                <div>
                    <label className="label-form text-slate-600 font-bold mb-1 block">Estado de Póliza *</label>
                    <select 
                        name="estadoPoliza" 
                        value={formData.estadoPoliza || ''} 
                        onChange={handleChange} 
                        className="input-round font-bold border-slate-200 focus:border-indigo-500 bg-slate-50/50 text-slate-700 w-full rounded-xl px-4 py-2.5 h-[46px] outline-none border transition-all focus:ring-1 focus:ring-indigo-100" 
                        required
                    >
                        <option value="">Seleccione...</option>
                        {estadoEmision.map(e => (
                            <option key={e} value={e}>{e}</option>
                        ))}
                    </select>
                </div>

                {/* NÚMERO DE CONTRATO (CON VALIDACIÓN DE DUPLICADOS EN TIEMPO REAL) */}
                <div className="relative text-left">
                    <label className="label-form text-slate-600 font-bold mb-1 block">N° de Contrato *</label>
                    
                    <div className="relative">
                        <input 
                            type="text" 
                            name="nContrato" 
                            value={formData.nContrato || ''} 
                            onChange={handleChange} 
                            onBlur={(e) => validarContratoDuplicado && validarContratoDuplicado(e.target.value)}
                            className={`input-round font-mono font-bold uppercase w-full rounded-xl px-4 py-2.5 h-[46px] outline-none border transition-all ${
                                contratoDuplicado 
                                    ? 'border-rose-500 text-rose-700 bg-rose-50/30 focus:border-rose-600 focus:ring-1 focus:ring-rose-200' 
                                    : 'border-slate-200 text-indigo-600 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-100'
                            }`}
                            placeholder="Ingrese N° Contrato"
                            required 
                        />

                        {/* Spinner discreto mientras valida en Firebase */}
                        {isValidatingContrato && (
                            <div className="absolute right-3 top-3">
                                <svg className="animate-spin h-5 w-5 text-indigo-500" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            </div>
                        )}
                    </div>

                    {/* CARTEL DE ADVERTENCIA DE CONTRATO DUPLICADO */}
                    {contratoDuplicado && (
                        <div className="mt-2 p-3 bg-rose-50 border border-rose-200 rounded-xl text-xs text-rose-800 font-medium animate-fade-in flex items-start gap-2.5 shadow-sm">
                            <span className="text-base leading-none">⛔</span>
                            <div>
                                <strong className="font-extrabold uppercase block tracking-wider text-rose-900 mb-0.5">
                                    ¡Contrato Ya Registrado!
                                </strong>
                                El N° <span className="font-mono font-bold text-rose-950">{contratoDuplicado.numContrato}</span> ya se encuentra cargado a nombre de:
                                <div className="font-black text-slate-900 mt-1 uppercase bg-white p-1.5 rounded-lg border border-rose-100 font-mono text-[11px] shadow-2xs">
                                    👤 {contratoDuplicado.clienteNombre} ({contratoDuplicado.compania})
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </fieldset>
    );
};

export default SectionDetalleArt;